import React, { Suspense } from "react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";

type ErrorBoundaryProps = {
  children: React.ReactNode;
  onReset?: () => void;
  fallback?: (reset: () => void, error: Error) => React.ReactNode;
};

type ErrorBoundaryState = { error: Error | null };

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  reset = () => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.error) {
      return this.props.fallback ? (
        this.props.fallback(this.reset, this.state.error)
      ) : (
        <DefaultErrorFallback error={this.state.error} onRetry={this.reset} />
      );
    }
    return this.props.children;
  }
}

function DefaultErrorFallback(props: {
  error: Error;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
      <div className="text-base font-bold">Etwas ist schiefgelaufen</div>
      <div className="text-sm text-ink-muted">{props.error.message}</div>
      <button
        onClick={props.onRetry}
        className="rounded-control border border-line px-3 py-1 text-sm hover:bg-surface"
      >
        Erneut versuchen
      </button>
    </div>
  );
}

/**
 * Kombiniert Suspense (Ladezustand) mit einer ErrorBoundary (Fehlerzustand).
 * `QueryErrorResetBoundary` setzt die fehlgeschlagenen Queries beim Retry
 * zurück, damit `useSuspenseQuery` erneut lädt.
 */
export function QueryBoundary(props: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  errorFallback?: (reset: () => void, error: Error) => React.ReactNode;
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary onReset={reset} fallback={props.errorFallback}>
          <Suspense
            fallback={
              props.fallback !== undefined ? props.fallback : <DefaultLoadingFallback />
            }
          >
            {props.children}
          </Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

function DefaultLoadingFallback() {
  return <div className="p-4 text-sm text-ink-muted">Laden…</div>;
}
